"use client";

import { Link } from "@/i18n/navigation";
import { safeUrl } from "@/lib/utils";
import { Banners as Banner } from "@prisma/client";
import { cn } from "cn";
import Autoplay from "embla-carousel-autoplay";
import Image from "next/image";
import React, { HTMLAttributes, useEffect, useState } from "react";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
    type CarouselApi,
} from "./shadcn/carousel";

const BannerCarousel: React.FC<
    { banners: Banner[] } & HTMLAttributes<HTMLDivElement>
> = ({ banners, className }) => {
    const [api, setApi] = useState<CarouselApi>();
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        if (!api) return;
        const onSelect = () => setSelectedIndex(api.selectedScrollSnap());
        onSelect();
        api.on("select", onSelect);
        return () => {
            api.off("select", onSelect);
        };
    }, [api]);

    const hasMultipleBanners = banners.length > 1;

    return (
        <div className={cn("w-full", className)}>
            <Carousel
                setApi={setApi}
                opts={{ loop: true }}
                plugins={[
                    Autoplay({
                        delay: 6000,
                        stopOnMouseEnter: true,
                        stopOnInteraction: true,
                    }),
                ]}
            >
                <CarouselContent>
                    {banners.map(({ Url, ImageUrl, Text }, index) => (
                        <CarouselItem
                            key={`banner-carousel-${ImageUrl}-${index}`}
                        >
                            <Link
                                href={Url ?? "#!"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="relative block h-92.5 w-full no-underline sm:aspect-555/154 sm:h-auto"
                            >
                                {ImageUrl && (
                                    <Image
                                        src={safeUrl(ImageUrl) ?? ""}
                                        alt={Text ?? ""}
                                        fill={true}
                                        sizes="100vw"
                                        priority={index === 0}
                                        className="absolute inset-0 h-full w-full object-cover sm:object-contain"
                                    />
                                )}
                            </Link>
                        </CarouselItem>
                    ))}
                </CarouselContent>
                {hasMultipleBanners && (
                    <>
                        <CarouselPrevious
                            variant="default"
                            className="left-4 rounded-md sm:left-10"
                        />
                        <CarouselNext
                            variant="default"
                            className="right-4 rounded-md sm:right-10"
                        />
                    </>
                )}
            </Carousel>
            {hasMultipleBanners && (
                <div className="mt-3 flex justify-center gap-2">
                    {banners.map((banner, index) => (
                        <button
                            key={`banner-dot-${banner.Id}`}
                            type="button"
                            aria-label={`${index + 1}. banner`}
                            aria-current={index === selectedIndex}
                            onClick={() => api?.scrollTo(index)}
                            className={cn(
                                "h-2 rounded-full transition-all",
                                index === selectedIndex
                                    ? "bg-primary w-8"
                                    : "bg-muted-foreground/30 hover:bg-muted-foreground/50 w-2",
                            )}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default BannerCarousel;
